<template>
    <div class="calendar">
        <vue-cal
            ref="vuecal" 
            locale="de"
            :time-from="startTime" 
            :time-to="endTime"
            :time-step="60" 
            :click-to-navigate="false"
            :time-cell-height="50"
            :selected-date="dateSelected"
            :events="calEvents"
            :split-days="splitDays"
            :sticky-split-labels="false"
            today-button
            @ready="init"
            @cell-click="cellClick"
            @view-change="viewChanged"
            :on-event-click="eventClick"
            :disable-views="['years']"
>

            <template slot="today-button">
                <button type="button" @click="chosenDate = new Date()">{{ __('components.calendar.today') }}</button>
            </template>

            <template slot="arrow-prev">
                <img src="../../../img/icons/arrow-prev-next.svg" />
            </template>
            <template slot="arrow-next">
                <img src="../../../img/icons/arrow-prev-next.svg" style="transform: rotate(180deg);" />
            </template>

            <template slot="title" slot-scope="{ title, view }">
                <template v-if="view.id == 'day'">
                    {{ view.startDate.toLocaleString('default', { day: '2-digit', weekday: 'long', month: 'long', year: 'numeric' }) }}
                </template>
                <template v-else>{{ title }}</template>
            </template>

            <template slot="weekday-heading" slot-scope="{ heading }">
                <span v-if="heading.date">
                    {{ heading.date.toLocaleString('default', { day: '2-digit', weekday: 'short', month: 'short' }) }}
                </span>
                <span v-else-if="heading.label">{{ heading.label }}</span>
                <span v-else-if="heading.small">{{ heading.small }}</span>
                <span v-else-if="heading.xsmall">{{ heading.xsmall }}</span>
                <span v-if="heading.dayOfMonth">&nbsp;{{ heading.dayOfMonth }}</span>
            </template>

            <template slot="event" slot-scope="{ event }">
                <div v-if="event.title" v-html="event.title" class="vuecal__event-title"></div>

                <div class="vuecal__event-time">
                    {{ $refs.vuecal.utils.date.formatTime(event.start) }}

                    <span v-if="event.endTimeMinutes">
                        &nbsp;- {{ $refs.vuecal.utils.date.formatTime(event.end) }}
                    </span>
                    <small class="days-to-end" v-if="event.daysCount > 1 && (event.segments[event.cellFormattedDate] || {}).isFirstDay">
                        &nbsp;+{{ event.daysCount - 1 }}{{ ($refs.vuecal.texts.day[0] || '').toLowerCase() }}
                    </small>
                </div>

                <div v-if="event.content" v-html="event.content" class="vuecal__event-content"></div>
                <div v-else-if="event.contentComponent" :is="event.contentComponent" class="vuecal__event-content"></div>
            </template>
        </vue-cal>

        <div class="calendar__pos-time" ref="posTimeTooltip">
            <img src="../../../img/icons/cross.svg" style="transform: rotate(45deg);" />
            {{ __('components.calendar.new_appointment_at') }}
            <span class="calendar__pos-time__time"></span>
        </div>
    </div>
</template>

<script>
import VueCal from 'vue-cal';
import 'vue-cal/dist/i18n/de.js'
import 'vue-cal/dist/vuecal.css';
import { toDate } from '../../helpers/common';
import { on, parentByClass, childIndex } from '../../helpers/dom';

export default {
    components: { VueCal },
    props: {
        events: {
            type: Array,
            default() {
                return []
            }
        },
        selectedDate: {
            type: Date,
            default() {
                return new Date();
            }
        },
        selectedEvent: {
            type: Object | Number
        },
        eventIdKey: {
            type: String,
            default: 'id'
        },
        splitDays: {
            type: Array,
            default() {
                return [];
            }
        },
        editable: {
            type: Boolean,
            default: true
        },
        minStartTime: {
            type: Number,
            default: 7 * 60
        },
        minEndTime: {
            type: Number,
            default: 19 * 60
        }
    },
    data() {
        return {
            chosenDate: this.selectedDate,
            currentView: null
        };
    },
    computed: {
        calEvents() {
            return this.events.map(ev => {
                return this.parseEventData(ev);
            });
        },
        dateSelected() {
            if(this.selectedEvent) {
                let selectedEvent = this.selectedEvent;
                
                if(typeof selectedEvent == 'number') {
                    selectedEvent = this.events.find(e => e[this.eventIdKey] == selectedEvent);
                }
                
                if(selectedEvent && typeof selectedEvent == 'object' && 'start' in selectedEvent) {
                    return toDate(selectedEvent.start);
                }
            }

            return this.chosenDate;
        },
        currentViewEvents() {
            return this.currentView
                ? this.calEvents.filter(p => p.end >= this.currentView.startDate && p.start <= this.currentView.endDate)
                : [];
        },
        startTime() {
            let startTime = this.minStartTime;

            for(const event of this.currentViewEvents) {
                const eventStartTime = (event.start.getHours() * 60);

                if(eventStartTime < startTime) {
                    startTime = eventStartTime;
                }
            }

            return startTime;
        },
        endTime() {
            let endTime = this.minEndTime;

            for(const event of this.currentViewEvents) {
                const eventEndTime = (event.end.getHours() * 60) + event.end.getMinutes();

                if(eventEndTime > endTime) {
                    endTime = eventEndTime;
                }
            }

            return endTime;
        }
    },
    watch: {
        events: {
            immediate: true,
            handler: function() {
                if(this.selectedEvent) {
                    this.setEventSelection(this.selectedEvent);
                }
            }
        },
        selectedDate: function(newDate) {
            this.chosenDate = newDate;
        },
        selectedEvent: {
            immediate: true,
            handler: function(newData, oldData) {
                this.setEventSelection(newData, oldData);
            }
        }
    },
    created() {
        this.mouseDown = false;
        this.mousePosData = {
            start: null,
            end: null,
        };
        this.dragCreateEventElem = null;
        this.handleCellClick = true;
        
        this.$emit('created');
    },
    methods: {
        init(readyEvent = null) {
            if(readyEvent && typeof readyEvent == 'object' && 'view' in readyEvent) {
                this.currentView = readyEvent;
            }
            
            if(this.editable) {
                this.initEditable();
            }

            const calContainer = this.$refs.vuecal.$el;

            // expand more than 2 overlapping events on mouseover
            on(calContainer, 'vuecal__event', 'mouseover', (target, e) => {
                if(this.$refs.vuecal.view.id == 'week' && parseInt(target.style.width, 10) < 50) {
                    const cellContentContainer = parentByClass(target, 'vuecal__cell-content');

                    if(!cellContentContainer.classList.contains('expand')) {
                        const cellContainer = cellContentContainer.parentElement;
                        const cellIndex = childIndex(cellContainer);

                        cellContentContainer.classList.remove('tighten', 'tighten-left', 'tighten-right');
                        cellContentContainer.classList.add('expand', 'expand-' + (cellIndex > 2 ? 'left' : 'right'));

                        for(const cell of cellContainer.parentElement.children) {
                            if(cell != cellContainer) {
                                cell.classList.add('opaque');
                            }
                        }
                    }
                }
            });

            calContainer.addEventListener('mouseout', e => {
                const sourceCell = parentByClass(e.target, 'vuecal__cell-content', calContainer, true);

                if(sourceCell && sourceCell.classList.contains('expand')) {
                    const targetCell = parentByClass(e.relatedTarget, 'vuecal__cell-content', calContainer, true);

                    if(sourceCell != targetCell) {
                        sourceCell.classList.add('tighten', sourceCell.classList.contains('expand-left') ? 'tighten-left' : 'tighten-right');
                        sourceCell.classList.remove('expand', 'expand-left', 'expand-right');

                        for(const cell of sourceCell.parentElement.parentElement.children) {
                            cell.classList.remove('opaque');
                        }
                    }
                }
                
            });
        },
        initEditable() {
            const calContainer = this.$refs.vuecal.$el;
            const calBody = calContainer.querySelector('.vuecal__body');
            const posTimeElem = this.$refs.posTimeTooltip.querySelector('.calendar__pos-time__time');

            calBody.addEventListener('mousemove', e => { 
                if(!this.dragCreateEventElem 
                    && (
                        (this.$refs.vuecal.view.id != 'week' && this.$refs.vuecal.view.id != 'day') 
                        || !e.target.classList 
                        || !e.target.classList.contains('vuecal__cell-content')
                    )
                ) {
                    this.$refs.posTimeTooltip.style.display = 'none';
                    return false;
                }

                let posTimeData = this.getPosTimeDataByMouseEvent(e);

                posTimeElem.innerText = this.timeOut(posTimeData.time);

                if(this.mousePosData.start && !this.dragCreateEventElem) {
                    this.dragCreateEventElem = document.createElement('div');
                    this.dragCreateEventElem.classList.add('calendar__drag-create-elem');
                    this.dragCreateEventElem.style.top = this.mousePosData.start.y + 'px';
                    this.dragCreateEventElem.style.bottom = 'auto';

                    this.mousePosData.start.cellData.node.querySelector('.vuecal__cell-content').appendChild(this.dragCreateEventElem);
                }
                
                if(this.dragCreateEventElem) {
                    this.$refs.posTimeTooltip.style.display = 'none';

                    let topDiff = this.mousePosData.start.y - posTimeData.coords.y;
                    let startDate = this.mousePosData.start.date;
                    let startTime = { hour: startDate.getHours(), minute: startDate.getMinutes() };
                    let endTime = posTimeData.time;

                    if(topDiff > 0) {
                        endTime = startTime;
                        startTime = posTimeData.time;
                    }
                    
                    this.dragCreateEventElem.style.height = Math.abs(topDiff) + 'px';
                    this.dragCreateEventElem.style.top = (this.mousePosData.start.y - (topDiff > 0 ? topDiff : 0)) + 'px';
                    this.dragCreateEventElem.innerHTML = '<span>' + this.timeOut(startTime) + ' - ' + this.timeOut(endTime) + '</span>';
                } else {    
                    this.$refs.posTimeTooltip.style.left = (e.clientX + 20) + 'px';
                    this.$refs.posTimeTooltip.style.top = (e.clientY + 5) + 'px';
                    this.$refs.posTimeTooltip.style.display = 'block';
                }
            });

            calBody.addEventListener('mouseenter', e => {
                this.$refs.posTimeTooltip.style.display = 'block';
            });
            calBody.addEventListener('mouseleave', e => {
                this.$refs.posTimeTooltip.style.display = 'none';

                if(this.dragCreateEventElem) {
                    this.dragCreateEventElem.remove();
                    this.dragCreateEventElem = null;
                }
            });

            calBody.addEventListener('mousedown', e => {
                if((this.$refs.vuecal.view.id != 'week' && this.$refs.vuecal.view.id != 'day') 
                    || !e.target.classList 
                    || !e.target.classList.contains('vuecal__cell-content')) 
                {
                    return false;
                }

                this.mousePosData.start = this.getPosDataByMouseEvent(e);
            });

            calBody.addEventListener('mouseup', e => {
                if(this.dragCreateEventElem) {
                    this.mousePosData.end = this.getPosDataByMouseEvent(e);

                    if(this.mousePosData.end) {
                        if(this.mousePosData.end.cellData.cell != this.mousePosData.start.cellData.cell) {
                            this.mousePosData.end.date.setDate(1);
                            this.mousePosData.end.date.setMonth(this.mousePosData.start.date.getMonth());
                            this.mousePosData.end.date.setYear(this.mousePosData.start.date.getFullYear());
                            this.mousePosData.end.date.setDate(this.mousePosData.start.date.getDate());
                        } else {
                            this.handleCellClick = false;
                        }

                        let dateStart = this.mousePosData.start.date;
                        let dateEnd = this.mousePosData.end.date;

                        if(dateStart > dateEnd) {
                            dateEnd = this.mousePosData.start.date;
                            dateStart = this.mousePosData.end.date;
                        }

                        if(dateEnd - dateStart > 0) {
                            this.$emit('drag-create-ended', { cal: this, start: dateStart, end: dateEnd });
                        }
                    }

                    this.dragCreateEventElem.remove();
                    this.dragCreateEventElem = null;
                }

                this.mousePosData.start = null;
                this.mousePosData.end = null;
            });

            document.addEventListener('keyup', e => {
                if(this.dragCreateEventElem && e.keyCode == 27) {
                    this.dragCreateEventElem.remove(),
                    this.dragCreateEventElem = null;
                    this.handleCellClick = false;
                }
            });
        },
        viewChanged(newViewData) {
            this.currentView = newViewData;
            this.$emit('view-change', newViewData);
        },
        cellClick(clickedDate) {
            if(!this.handleCellClick) {
                this.handleCellClick = true;
                return false;
            }

            if(!(clickedDate instanceof Date) && clickedDate && typeof clickedDate == 'object' && 'date' in clickedDate) {
                clickedDate = clickedDate.date;
            }

            if(clickedDate 
                && clickedDate instanceof Date 
                && (this.$refs.vuecal.view.id == 'week' || this.$refs.vuecal.view.id == 'day')
                && !(clickedDate.getHours() == 0 && clickedDate.getMinutes() == 0 && clickedDate.getSeconds() == 0)
            ) {
                let minutes = clickedDate.getMinutes();
                minutes = Math.round(minutes / 10) * 10;

                if(minutes == 60) {
                    clickedDate.addHours(1);
                    minutes = 0;
                }

                clickedDate.setMinutes(minutes);
                
                this.$emit('date-clicked', { cal: this, date: clickedDate });
            }
        },
        eventClick(eventData, mouseEvent) {
            let clickEvent = mouseEvent.target.getAttribute('data-click-event');

            if(clickEvent) {
                let clickEventData = mouseEvent.target.getAttribute('data-click-data');
                this.$emit('click-' + clickEvent, { mouseEvent: mouseEvent, cal: this, data: clickEventData, eventData: eventData });  
            } else {
                this.$emit('event-clicked', { cal: this, data: eventData });  
            }

            this.handleCellClick = false;
        },
        addEvent(data) {
            this.events.push(data);
        },
        changeEvent(id, newData = undefined) {
            let eventIndex = this.events.findIndex(e => e[this.eventIdKey] == id);

            if(eventIndex != -1) {
                if(newData == undefined) {
                    this.events.splice(eventIndex, 1);
                } else {
                    this.events.splice(eventIndex, 1, newData);
                }
            } else {
                throw new Error(`Could not find event with eid ${eid}`);
            }
        },
        removeEvent(id) {
            this.changeEvent(id);
        },
        setEventSelection(newSelection) {
            if(typeof newSelection == 'number') {
                newSelection = this.events.find(e => e[this.eventIdKey] == newSelection);
            }

            this.events.forEach((event, index) => {
                let selectedClassIndex = event.classes ? event.classes.indexOf('selected') : -1;
                if(selectedClassIndex != -1) {
                    event.classes.splice(selectedClassIndex, 1);
                }
            });
            
            if(newSelection && this.eventIdKey in newSelection) {
                let eventDataIndex = this.events.findIndex(e => e[this.eventIdKey] == newSelection[this.eventIdKey]);

                if(eventDataIndex != -1) {
                    let eventClasses = (this.events[eventDataIndex].classes || []);

                    if(eventClasses.indexOf('selected') == -1) {
                        eventClasses.push('selected');
                    }
                }
            }
        },
        parseEventData(data) {
            let eventData = Object.assign({}, data);

            eventData['start'] = toDate(eventData['start']);
            eventData['end'] = toDate(eventData['end']);

            if('classes' in eventData) {
                eventData['class'] = eventData.classes.join(' ');
            }

            return eventData;
        },
        getPosDataByMouseEvent(mouseEvent) {
            let cellData = this.getCellByNode(mouseEvent.target);

            if(cellData) {
                let posTimeData = this.getPosTimeDataByMouseEvent(mouseEvent);

                let posDate = new Date(cellData.cell.formattedDate);
                posDate.setHours(posTimeData.time.hour);
                posDate.setMinutes(posTimeData.time.minute);

                return {
                    date: posDate,
                    cellData: cellData,
                    x: posTimeData.coords.x,
                    y: posTimeData.coords.y
                };
            }

            return null;
        },
        getPosTimeDataByMouseEvent(mouseEvent) {
            let posInfo = this.$refs.vuecal.minutesAtCursor(mouseEvent);
                
            let posHour = Math.floor(posInfo.minutes / 60);
            let posMinute = Math.round((posInfo.minutes % 60) / 10) * 10;

            if(posMinute == 60) {
                posHour++;
                posMinute = 0;
            }

            return {
                time: {
                    hour: posHour,
                    minute: posMinute
                },
                coords: posInfo.cursorCoords
            };
        },
        getCellByNode(node) {
            while((!node.classList || !node.classList.contains('vuecal__cell')) && node.parentElement) {
                node = node.parentElement;
            }

            if(!node.classList.contains('vuecal__cell')) {
                return null;
            }

            let nodeIndex = 0, elem = node;
            while((elem = elem.previousElementSibling) != null) ++nodeIndex;

            let calCells = this.$refs.vuecal.viewCells;

            if(calCells.length > nodeIndex) {
                return {
                    node: node,
                    nodeIndex: nodeIndex,
                    cell: calCells[nodeIndex]
                };
            } else {
                return null;
            }
        },
        timeOut(time) {
            return (time.hour < 10 ? '0' : '') + time.hour + ':' + (time.minute < 10 ? '0' : '') + time.minute;
        }
    }
}
</script>

<style lang="scss">
.calendar {
    &__pos-time {
        position: fixed;
        display: none;
    }

    &__drag-create-elem {
        position: absolute;
        left: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        z-index: 5;
    }
}
</style>